import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import setObj from "lodash/set";
import _ from "lodash";

import { STATUS } from "dot/Components/Misc/CaseDetails_System";
import { findBoqLineIndex } from "dot/Utils/matchBoqProductLine";
import { setProp } from "Store/Actions";
import { setPropT } from 'Store/Actions/ts';

export interface ProductQuantityMap {
    [key: string]: number;
}

interface ProductCheckListRow {
    quantity: number;
    product_code?: string;
    productCode: string;
}

interface SetQuantityByDeltaPayload {
    productCode: string;
    delta: number;
}

export interface SelectedProductQuantity {
    product_code: string;
    quantity: number;
}

export interface UpdatelistPayloadType {
    activeStep: string;
    useCase: string;
    selectName: string;
    device: Device;
}
export interface Device {
    id: string;
    product_id: string;
    quantity: number;
    product_description: string;
    product_code: string;
    status: string;
    product_kits?: (ProductKitsEntity)[] | null;
    memory_capacity: string;
    pod_capacity: string;
    storage_capacity: string;
    vcpu_capacity: string;
    IRP: string;
    checked: boolean;
    cost: number;
    default_list_price: number;
    list_price: number;
}
export interface ProductKitsEntity {
    product_kit_code: string;
    product_kit_description: string;
    product_kit_quantity: string;
    percentage: string;
}

/**
 * Snapshot of `data.boq` taken before the BoQ is edited, used to restore/compare later.
 * Holds either the BoQ item array or an index-keyed copy of it (from `{ ...boq }`).
 */
export type DynamicBoqBackup =
    | Record<string, unknown>[]
    | Record<string, Record<string, unknown>>;


const productQuantityMap: ProductQuantityMap = {};
const selectedProductsData: SelectedProductQuantity[] = [];
export const initialData = {
    element: {},
    dataSet: {},
    callBack: {},
    validationDetails: {},
    sid: "",
    boq: [],
    actionNext: false,
    tabSelect: 'Full Solutions',
    reference_solution: {
        solution: "NDAC",
        commercial_model: "Opex",
        contract_duration: 1,
        ndacPlanner: {},
        linkedPlanner: {}
    },
    selected_ndac_services: [],
    finalCombined_ndac_services: [],
    elementMMS: {},
    undoBoq: [],
    pageFeedbacks: [],
    notification_messages: {},
    callbackdefaults: {},
    eot_response_syncoffer: [],
    setTargetFields: {},
    setAccessoriesButton: true,
    localStates: {
        setOptionsData: {},
        setOptionsDataList: {},
        setSearchButtonStatus: false,
        setIsLoading: false,
        setProdCatalogApiLoad: false,
        setProductDataList: {},
        setConsumptionGraph: false,
        subStepState: {},
    },
    dataFromPackage: {},
    modal: {},
    stepFormInputs: {},
    package_options_count: 0,
    addProductClicked: false,
    intermediateConfigResponse: {},
    dynamicboqBakup: {} as DynamicBoqBackup,
    isCustomiseSaveDisabled: true,
    catalogCallBack: "",
    productQuantityMap,
    selectedProductsData,
};
const dynamicSlice = createSlice({
    initialState: {
        data: initialData,
        status: STATUS.IDLE,
    },
    name: 'dynamic',
    reducers: {
        setElement(state, action) {
            state.data.element = action.payload
        },
        setArrayElement(state, action) {
            if (action.payload.method === "add") {
                state.data.element[action.payload.activeStep][action.payload.useCase].push(action.payload.data);
            }
            if (action.payload.method === "remove") {
                state.data.element[action.payload.activeStep][action.payload.useCase] = state.data.element[action.payload.activeStep][action.payload.useCase].filter((e) => e.key !== action.payload.data.key);
            }
        },
        setCallBack(state, action) {
            state.data.callBack = action.payload
        },
        resetDynamicStep(state,) {
            state.data = { ...initialData }
            state.status = STATUS.IDLE;
        },
        setQuantityList(state, action: PayloadAction<UpdatelistPayloadType>) {
            if (action.payload.device.product_code) {
                const typedQuantity = typeof action.payload.device.quantity === 'number' ? action.payload.device.quantity : Number(action.payload.device.quantity);
                state.data.productQuantityMap[action.payload.device.product_code] = typedQuantity;
            }
            state.data.element[action.payload.activeStep][action.payload.useCase][action.payload.selectName] = state.data.element[action.payload.activeStep][action.payload.useCase][action.payload.selectName].map((e) => e.id == action.payload.device.id ? action.payload.device : e);
        },
        setSelectedNdacServices(state, action) {
            // console.log(action.payload, 'ndac payload')
            if (action.payload.method === "add") {
                const value = action.payload.data

                state.data.selected_ndac_services.push(value)
            }
        },
        setDataSet(state, action) {
            let newData = { ...state.data.dataSet };
            for (let key in action.payload) {
                if ((Object.keys(newData)).includes(key)) {
                    newData[key] = action.payload[key];
                }
            }
            if (action.payload.method === "remove") {
                state.data.selected_ndac_services = state.data.selected_ndac_services.filter((e) => e.product_id !== action.payload.data)
            }
        },
        // localstates used from options moved here
        setState(state, action) {
            state.data.localStates = action.payload;
        },

        setCount_of_data(state, action) {
            let newData = { ...state.data.element };
            for (let step in action.payload) {
                if ((Object.keys(newData)).includes(step)) {
                    for (let key in action.payload[step]) {
                        newData[step][key] = action.payload[step][key];
                    }
                }
            }
            state.data.element = { ...newData };
        },

        // common function to dispatch batch data for dynamic slice
        setDynamicBatchData(state, action) {
            let newData = { ...state.data[action.payload['slice']] };

            for (let eachKey in action.payload['data']) {
                if ((Object.keys(newData)).includes(eachKey)) {
                    if (action.payload['data'][eachKey] === null) {
                        newData[eachKey] = null;
                    } else {
                        for (let key in action.payload['data'][eachKey]) {
                            newData[eachKey][key] = action.payload['data'][eachKey][key];
                        }
                    }
                }
            }
            state.data[action.payload['slice']] = { ...newData };
        },

        updateElementDataOfStep(state, action) {
            const { stepName, stepData } = action.payload;
            state.data.element = {
                ...state.data.element,
                [stepName]: {
                    ...state.data.element[stepName], // retain the other data in the step
                    ...stepData // merge the updated step data
                }
            }
        },
        setStepConnectivityCategory(state, action) {
            state.data.element[action.payload.activeStep]["category"] = action.payload.category;
        },

        /**
         * Updates saleItems array in intermediateConfigResponse.responseData.boqData.data
         * @param {Object} action.payload - Contains action type and data
         * @param {string} action.payload.action - 'delete' or 'qtyChange'
         * @param {Object} action.payload.boQDeleteKey - Item to delete (for delete action)
         * @param {string} action.payload.productCode - Product code to find (for qtyChange action)
         * @param {number} action.payload.newQuantity - New quantity value (for qtyChange action)
         */
        updateSaleItemsInIntermediateResponse(state, action) {
            const { action: actionType, boQDeleteKey, productCode, newQuantity, skipQtyPriceMultiply } = action.payload;

            // Check if intermediateConfigResponse and nested structure exists
            const intermediateResponse = state.data.intermediateConfigResponse as any;
            
            if (!intermediateResponse?.responseData?.boqData?.data?.saleItems) {
                return;
            }

            const saleItems = intermediateResponse.responseData.boqData.data.saleItems;

            if (actionType === 'delete' && boQDeleteKey) {
                const deleteIndex = findBoqLineIndex(saleItems, boQDeleteKey);
                if (deleteIndex !== -1) {
                    intermediateResponse.responseData.boqData.data.saleItems = saleItems.filter(
                        (_item: any, index: number) => index !== deleteIndex
                    );
                }
            }

            if (actionType === 'qtyChange' && productCode && newQuantity !== undefined) {
                const lineKey = {
                    product_id: productCode,
                    productTitle: action.payload.productTitle,
                    workItem: action.payload.workItem,
                };
                const saleItemIndex = findBoqLineIndex(saleItems, lineKey);
                if (saleItemIndex === -1) {
                    return;
                }
                const item = saleItems[saleItemIndex];
                const updatedItem = { ...item, quantity: newQuantity };
                const skipItemQtyPriceMultiply =
                    skipQtyPriceMultiply === true ||
                    (item.isServiceOnly === true && item.productFamily !== 'NDAC');
                if (!skipItemQtyPriceMultiply && item.IRP !== undefined) {
                    updatedItem.totalIRP = item.IRP * newQuantity;
                }
                const nextSaleItems = [...saleItems];
                nextSaleItems[saleItemIndex] = updatedItem;
                intermediateResponse.responseData.boqData.data.saleItems = nextSaleItems;
            }

            if (_.isEmpty(state?.data?.dynamicboqBakup) || _.isEmpty(state?.data?.intermediateConfigResponse)) {
                state.data.isCustomiseSaveDisabled = true;
            } else {
                state.data.isCustomiseSaveDisabled = false;
            }
        },

        updateDynamicBoqBakup(state, action: PayloadAction<DynamicBoqBackup>) {
            state.data.dynamicboqBakup = action.payload;
        },

        setProductQuantityMap(state, action: PayloadAction<ProductCheckListRow[]>) {
            state.data.productQuantityMap = action.payload.reduce((acc, curr) => {
                acc[curr.product_code ?? curr.productCode] = typeof curr.quantity === 'number' ? curr.quantity : Number(curr.quantity);
                return acc;
            }, {} as ProductQuantityMap);
        },
        setIncrementQuantity(state, action: PayloadAction<ProductCheckListRow>) {
            const { productCode, quantity } = action.payload;
            const typepedQuantity = typeof quantity === 'number' ? quantity : Number(quantity);
            if (productCode) {
                if (typeof state.data.productQuantityMap[productCode] === 'number') {
                    state.data.productQuantityMap[productCode] = state.data.productQuantityMap[productCode] + typepedQuantity;
                } else {
                    state.data.productQuantityMap[productCode] = typepedQuantity;
                }
            }
        },
        setDecrementQuantity(state, action: PayloadAction<ProductCheckListRow>) {
            const { productCode, quantity } = action.payload;
            const typepedQuantity = typeof quantity === 'number' ? quantity : Number(quantity);
            if (productCode) {
                if (typeof state.data.productQuantityMap[productCode] === 'number') {
                    state.data.productQuantityMap[productCode] = state.data.productQuantityMap[productCode] - typepedQuantity;
                } else {
                    state.data.productQuantityMap[productCode] = typepedQuantity;
                }
            }
        },
        setQuantity(state, action: PayloadAction<ProductCheckListRow>) {
            const { productCode, quantity } = action.payload;
            const typepedQuantity = typeof quantity === 'number' ? quantity : Number(quantity);
            if (productCode) {
                state.data.productQuantityMap[productCode] = typepedQuantity;
            }
        },
        // Applies a signed qty difference (positive adds, negative subtracts), e.g. from a typed quantity change.
        setQuantityByDelta(state, action: PayloadAction<SetQuantityByDeltaPayload>) {
            const { productCode, delta } = action.payload;
            const typedDelta = typeof delta === 'number' ? delta : Number(delta);
            if (typeof state.data.productQuantityMap[productCode] === 'number' && !isNaN(typedDelta) && productCode) {
                state.data.productQuantityMap[productCode] = state.data.productQuantityMap[productCode] + typedDelta;
            }
        }
    },
    // extra reducers for dynamic slice
    extraReducers(builder) {
        builder.addCase(setProp, (state, action: setPropT) => {
            if (action.payload.slice === dynamicSlice.name) {
                setObj(state.data, action.payload.key, action.payload.value);
            }
        })
    }
});

export default dynamicSlice.reducer;
export const {
    setElement,
    setArrayElement,
    setCallBack,
    resetDynamicStep,
    setQuantityList,
    setSelectedNdacServices,
    setDataSet,
    setCount_of_data,
    updateElementDataOfStep,
    setDynamicBatchData,
    setStepConnectivityCategory,
    updateSaleItemsInIntermediateResponse,
    setProductQuantityMap,
    setIncrementQuantity,
    setDecrementQuantity,
    setQuantityByDelta,
    setQuantity,
    updateDynamicBoqBakup
} = dynamicSlice.actions;
